import { Fragment, type ReactNode } from 'react'

/**
 * Regex de URL conservadora: aceita http:// e https://, captura até o
 * próximo whitespace ou aspas. Pontuação final comum (`.,;:!?)`]`}) é
 * removida do match e devolvida como texto plano — evita que `(http://...)`
 * vire `(http://...)` clicável.
 */
const URL_REGEX = /https?:\/\/[^\s<>"']+/g

const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/

/**
 * Converte um texto em JSX, transformando URLs `http(s)` em links clicáveis
 * que abrem em nova aba com `rel=noopener noreferrer` (segurança). Outros
 * schemes (`javascript:`, `data:`, etc.) são tratados como texto puro.
 *
 * Preserva quebras de linha do texto original (via `whitespace-pre-wrap`
 * no consumidor — esta função não envolve em `<br>`).
 */
export function linkifyText(text: string): ReactNode {
  if (!text) return text

  const nodes: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  // Reset porque regex é global e mantém `lastIndex` entre chamadas.
  URL_REGEX.lastIndex = 0

  while ((match = URL_REGEX.exec(text)) !== null) {
    const start = match.index
    const fullMatch = match[0]
    const trailing = fullMatch.match(TRAILING_PUNCTUATION)?.[0] ?? ''
    const url = trailing ? fullMatch.slice(0, -trailing.length) : fullMatch

    if (start > lastIndex) {
      nodes.push(
        <Fragment key={`t-${lastIndex}`}>{text.slice(lastIndex, start)}</Fragment>,
      )
    }
    nodes.push(
      <a
        key={`l-${start}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 underline decoration-blue-200 underline-offset-2 hover:decoration-blue-500"
      >
        {url}
      </a>,
    )
    if (trailing) {
      nodes.push(<Fragment key={`p-${start}`}>{trailing}</Fragment>)
    }
    lastIndex = start + fullMatch.length
  }

  if (lastIndex < text.length) {
    nodes.push(
      <Fragment key={`t-${lastIndex}`}>{text.slice(lastIndex)}</Fragment>,
    )
  }

  return nodes
}
